import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import type { CustomPlanLimits, Plan } from '@brix/types'

export interface PlatformAdminTenant {
  id: string
  name: string
  slug: string
  plan: string
  created_at: string
  site_count: number
  member_count: number
  owner_email: string | null
  subscription_status: string | null
  current_period_end: string | null
}

export interface PlatformAdminTenantDetail {
  tenant: {
    id: string
    name: string
    slug: string
    plan: string
    custom_limits: CustomPlanLimits | null
    created_at: string
    updated_at: string
  }
  subscription: {
    stripe_customer_id: string | null
    stripe_subscription_id: string | null
    plan: string
    status: string
    current_period_end: string | null
    cancel_at_period_end: boolean
  } | null
  sites: Array<{
    id: string
    name: string
    subdomain: string
    customDomain: string | null
    published: boolean
    createdAt: string
  }>
  members: Array<{
    id: string
    name: string | null
    role: string
    email: string
    createdAt: string
  }>
}

// Whether the current user is a platform admin — independent of tenant
// membership/role. Cached so both the guard and the Sidebar link can
// share one request.
export function usePlatformAdminStatus() {
  const user = useAuthStore((s) => s.user)
  return useQuery({
    queryKey: ['platform-admin-status', user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_platform_admin')
      if (error) throw error
      return !!data
    },
  })
}

export function usePlatformAdminTenants() {
  return useQuery({
    queryKey: ['platform-admin-tenants'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_platform_admin_tenants')
      if (error) throw error
      return (data ?? []) as PlatformAdminTenant[]
    },
  })
}

export function usePlatformAdminTenantDetail(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['platform-admin-tenant-detail', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) throw new Error('tenantId is required')
      const { data, error } = await supabase.rpc('get_platform_admin_tenant_detail', { p_tenant_id: tenantId })
      if (error) throw error
      return data as unknown as PlatformAdminTenantDetail
    },
  })
}

export interface SetTenantCustomPlanInput {
  tenantId: string
  limits: CustomPlanLimits
}

// Assigns (or edits) a negotiated custom plan on a tenant — platform-admin
// only, gated server-side by set_tenant_custom_plan()'s is_platform_admin() guard.
export function useSetTenantCustomPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ tenantId, limits }: SetTenantCustomPlanInput) => {
      const { error } = await supabase.rpc('set_tenant_custom_plan', {
        p_tenant_id: tenantId,
        p_site_limit: limits.siteLimit,
        p_page_limit: limits.pageLimit,
        p_post_limit: limits.postLimit,
        p_can_view_analytics: limits.canViewAnalytics,
        p_can_use_ai_assistant: limits.canUseAiAssistant,
        p_can_use_pro_blocks: limits.canUseProBlocks,
      })
      if (error) throw error
    },
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: ['platform-admin-tenant-detail', tenantId] })
      void qc.invalidateQueries({ queryKey: ['platform-admin-tenants'] })
    },
  })
}

export interface ClearTenantCustomPlanInput {
  tenantId: string
  fallbackPlan: Exclude<Plan, 'custom'>
}

// Reverts a tenant off the custom plan back to one of the 4 fixed tiers.
export function useClearTenantCustomPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ tenantId, fallbackPlan }: ClearTenantCustomPlanInput) => {
      const { error } = await supabase.rpc('clear_tenant_custom_plan', {
        p_tenant_id: tenantId,
        p_fallback_plan: fallbackPlan,
      })
      if (error) throw error
    },
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: ['platform-admin-tenant-detail', tenantId] })
      void qc.invalidateQueries({ queryKey: ['platform-admin-tenants'] })
    },
  })
}
